import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { BookingStates } from '../../../../../../constants/booking-states';
import { Tooltip, TooltipData } from '../../../../elements-tooltip/tooltip';
import { checkIsGuestView } from '../../utils/check-is-guest-view';
import { getCurrencyFromListing } from '../../utils/get-currency-from-listing';
import { BookingSummaryItem } from '../BookingSummaryItem';
import { AddOnsTaxesAndFees } from './AddOnsTaxesAndFees';
import { PriceBreakdownAddOns } from './PriceBreakdownAddOns';
import { PriceBreakdownDayRate } from './PriceBreakdownDayRate';
import { PriceBreakdownDiscounts } from './PriceBreakdownDiscounts';
import { PriceBreakdownSubtotal } from './PriceBreakdownSubtotal';

const getLabel = (booking, isGuestView) => {
  if (isGuestView && booking.obj_state === BookingStates.CANCELLED) {
    return (
      <FormattedMessage
        id="inbox.create_calculator.refunded"
        defaultMessage="PRICE (REFUNDED)"
      />
    );
  }
  return (
    <FormattedMessage
      id="inbox.fee_calc_price_breakdown.space"
      defaultMessage="Space Rental"
    />
  );
};

const getProcessingFee = (guestCalculations, booking, isGuestView) => {
  if (!guestCalculations && booking?.error) {
    return 0;
  }
  const fee = guestCalculations?.fee || booking?.guest_payment_processing_fee;

  return isGuestView ? fee : -fee;
};

export const BookingPriceBreakdown = ({
  booking,
  listing,
  profile,
  masterContract,
  addOnsContracts
}) => {
  const isGuestView = checkIsGuestView(profile, booking);
  const { dayRateDiscount, otherDiscounts } = (booking.discounts || []).reduce(
    (map, curr) => {
      const discountType = curr.meta?.type || curr.type;
      if (discountType === 'DAY_RATE_DISCOUNT') {
        map.dayRateDiscount = curr;
        return map;
      }

      const otherDiscArr = map.otherDiscounts || [];
      otherDiscArr.push(curr);
      map.otherDiscounts = otherDiscArr;
      return map;
    },
    {}
  );

  const masterCalculations = masterContract?.calculations;
  const guestCalculations = masterCalculations?.guest;

  const currency = getCurrencyFromListing(listing);

  return (
    <BookingSummaryItem
      testingId="summary-price-breakdown"
      label={getLabel(booking, isGuestView)}
    >
      <PriceBreakdownSubtotal booking={booking} listing={listing} />

      {!!dayRateDiscount && (
        <div className="margin-sm-top">
          <PriceBreakdownDayRate
            currency={currency}
            discount={dayRateDiscount.meta || dayRateDiscount}
          />
        </div>
      )}

      {!isEmpty(booking.addtl_charges) && (
        <div
          className="margin-sm-top"
          data-testing-id="price-breakdown-addtl-charges"
        >
          {booking.addtl_charges.map((charge, i) => {
            return (
              <div
                key={`additional-charge-${i}`}
                className="flex space-between"
                data-testing-id="addtl-charge-container"
              >
                <span className="summary-item-text">
                  {charge.charge_str}
                  {!!TooltipData[charge.type] && (
                    <span className="margin-sm-left">
                      <Tooltip tooltip={TooltipData[charge.type]} />
                    </span>
                  )}
                </span>
                <span data-testing-id="addtl-charges-amount">
                  <FormattedNumber
                    value={charge.value}
                    currency={currency}
                    style="currency"
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!!otherDiscounts?.length && (
        <div className="margin-sm-top">
          <PriceBreakdownDiscounts
            discounts={otherDiscounts}
            currency={currency}
          />
        </div>
      )}

      <div className="margin-sm-top">
        <span className="summary-item-text">
          {
            <span
              className="inline-block"
              data-testing-id="price-breakdown-fees-label"
            >
              {isGuestView ? (
                <FormattedMessage
                  id="inbox.create_calculator.processing"
                  defaultMessage="Processing"
                />
              ) : (
                <FormattedMessage
                  id="inbox.create_calculator.service_fee"
                  defaultMessage="Service Fee"
                />
              )}
              <span className="margin-sm-left">
                <Tooltip
                  tooltip={
                    TooltipData[isGuestView ? 'PROCESSING_FEE' : 'SERVICE_FEE']
                  }
                />
              </span>
            </span>
          }
        </span>
        <span
          className="pull-right"
          data-testing-id="price-breakdown-fees-amount"
        >
          {
            <FormattedNumber
              value={getProcessingFee(guestCalculations, booking, isGuestView)}
              currency={currency}
              style="currency"
            />
          }
        </span>
      </div>

      {guestCalculations?.us_sales_tax_space_rental > 0 && (
        <div className="margin-sm-top">
          <span className="summary-item-text">
            <span className="inline-block">
              <FormattedMessage
                id="price_table.space_rental_sales_tax"
                defaultMessage="Space rental sales tax"
              />
            </span>
          </span>
          <span
            className="pull-right"
            data-testing-id="price-breakdown-subtotal-sales-tax"
          >
            <FormattedNumber
              value={guestCalculations.us_sales_tax_space_rental}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      )}

      {masterContract?.split_payment_checkout && (
        <div className="margin-sm-top" data-testing-id="price-breakdown-sp">
          <span className="summary-item-text">
            <span className="inline-block">
              <FormattedMessage
                id="inbox.price_table.part_now_part_later_fee"
                defaultMessage="Part now, part later fee"
              />
            </span>
          </span>
          <span
            className="pull-right"
            data-testing-id="price-breakdown-sp-amount"
          >
            <FormattedNumber
              value={masterCalculations?.guest?.split_payment_fee_amount}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      )}

      {!!addOnsContracts?.length && (
        <div className="margin-lg-top">
          <PriceBreakdownAddOns
            masterContract={masterContract}
            addOnsContracts={addOnsContracts}
            currency={currency}
            profile={profile}
          />
        </div>
      )}

      {(!isEmpty(masterCalculations?.add_on_taxes_and_fees) ||
        guestCalculations?.us_sales_tax_add_ons > 0) && (
        <div className="margin-sm-top">
          <AddOnsTaxesAndFees
            masterContract={masterContract}
            currency={currency}
          />
        </div>
      )}

      <hr className="w-100" />

      <div className="flex space-between text-gray-900">
        <h5 className="no-margin text-base">
          <FormattedMessage
            id="inbox.render_fee_calc.total"
            defaultMessage="Total"
          />
        </h5>
        <h5 className="no-margin text-base" data-testing-id="total-amount">
          <FormattedNumber
            value={masterCalculations?.guest?.payment || 0}
            currency={currency}
            style="currency"
          />
        </h5>
      </div>
    </BookingSummaryItem>
  );
};

BookingPriceBreakdown.propTypes = {
  booking: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  masterContract: PropTypes.object.isRequired,
  addOnsContracts: PropTypes.arrayOf(PropTypes.object)
};
